/**
 * SafetyNetModal — the single "Need Help Now?" safety experience.
 * Opened from the persistent Shield in the top nav AND by the AvA when a
 * teen asks to talk to a real person (warm handover is paused — Dec 2026).
 * 3 steps: how urgent → freephone helplines (dial/copy/QR) → copyable message.
 * Every interaction is written to the black-box safety audit log.
 */
import React, { useState, useEffect } from 'react';
import { Shield, Phone, MessageCircle, Copy, Check, ChevronLeft, X, Heart, Globe } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import detectRegion from '../utils/detectRegion';
import apiClient from '../api/client';

const SCRIPT_TEXT = "Hey, it's me. I'm having a really hard time right now and I didn't know how to say it out loud, so I'm sending this instead. Could you call me or come find me when you see this? I just need someone with me for a bit.";

const DISCLAIMER_VERSION = '2026-12-v2';
const DISCLAIMER = "These helplines are independent organisations with their own trained human listeners — they are not part of HiBFF. From the moment you call, text or visit them, everything is between you and them: HiBFF has no involvement in, and accepts no responsibility for, anything said or anything that happens afterwards. HiBFF never logs these calls. HiBFF is a social-skills practice platform — not a mental-health or emergency service.";

// FREE local helplines, chosen by the teen's region (locale/timezone based)
const REGION_DATA = {
  uk: {
    emergency: { label: 'Call 999 now', tel: '999' },
    lines: [
      { name: 'Samaritans', detail: 'Free, 24/7', number: '116 123', tel: '116123' },
      { name: 'Childline', detail: 'Under 19s, free', number: '0800 1111', tel: '08001111' },
    ],
  },
  us: {
    emergency: { label: 'Call 911 now', tel: '911' },
    lines: [
      { name: 'Suicide & Crisis Lifeline', detail: 'Call or text, free 24/7', number: '988', tel: '988' },
      { name: 'Boys Town National Hotline', detail: 'Teens & families, free 24/7', number: '1-800-448-3000', tel: '18004483000' },
    ],
  },
  ca: {
    emergency: { label: 'Call 911 now', tel: '911' },
    lines: [
      { name: 'Kids Help Phone', detail: 'Free, 24/7', number: '1-800-668-6868', tel: '18006686868' },
      { name: 'Suicide Crisis Helpline', detail: 'Call or text, free 24/7', number: '988', tel: '988' },
    ],
  },
  au: {
    emergency: { label: 'Call 000 now', tel: '000' },
    lines: [
      { name: 'Kids Helpline', detail: 'Ages 5-25, free 24/7', number: '1800 55 1800', tel: '1800551800' },
      { name: 'Lifeline', detail: 'Free, 24/7', number: '13 11 14', tel: '131114' },
    ],
  },
  ie: {
    emergency: { label: 'Call 112 now', tel: '112' },
    lines: [
      { name: 'Samaritans', detail: 'Free, 24/7', number: '116 123', tel: '116123' },
      { name: 'Childline', detail: 'Under 18s, free', number: '1800 66 66 66', tel: '1800666666' },
    ],
  },
  nz: {
    emergency: { label: 'Call 111 now', tel: '111' },
    lines: [
      { name: 'Youthline', detail: 'Free, 24/7', number: '0800 376 633', tel: '0800376633' },
      { name: 'Need to Talk?', detail: 'Free call or text, 24/7', number: '1737', tel: '1737' },
    ],
  },
  international: {
    emergency: { label: 'Call your local emergency number', tel: '112' },
    lines: [],
  },
};

const isPhoneDevice = () =>
  typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

export const SafetyNetModal = ({ onClose, source = 'shield', emergency = false }) => {
  const [step, setStep] = useState(emergency ? 2 : 1);
  const [urgency, setUrgency] = useState(emergency ? 'danger' : null);
  const [copied, setCopied] = useState(false);
  const [copiedTel, setCopiedTel] = useState(null);
  const region = detectRegion();
  const regionData = REGION_DATA[region] || REGION_DATA.international;
  const onPhone = isPhoneDevice();

  // Black-box audit trail — every safety interaction, timestamped.
  const audit = (event, detail = {}) => {
    apiClient.request('/api/safety/audit-log', {
      method: 'POST',
      body: JSON.stringify({ event, source, region, disclaimer_version: DISCLAIMER_VERSION, detail }),
    }).catch(() => {});
  };

  useEffect(() => {
    audit('modal_shown', { trigger: source, emergency: String(emergency) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const close = () => {
    audit('modal_closed', { at_step: String(step) });
    onClose();
  };

  const pick = (u) => {
    setUrgency(u);
    setStep(2);
    audit('option_picked', { urgency: u });
  };

  const copyNumber = async (h) => {
    try {
      await navigator.clipboard.writeText(h.number);
      setCopiedTel(h.tel);
      setTimeout(() => setCopiedTel(null), 3000);
    } catch { /* older browsers */ }
  };

  const handleLineClick = (h) => {
    audit('helpline_clicked', { name: h.name, number: h.number, device: onPhone ? 'phone' : 'desktop' });
    if (!onPhone) copyNumber(h);
  };

  const copyScript = async () => {
    try {
      await navigator.clipboard.writeText(SCRIPT_TEXT);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      audit('message_copied', {});
    } catch { /* older browsers */ }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center px-4" data-testid="safety-net-modal">
      <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl relative max-h-[92vh] overflow-y-auto">
        <button onClick={close} data-testid="safety-net-close" className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-gray-100 text-gray-400">
          <X size={18} />
        </button>

        <div className="flex items-center gap-2.5 mb-1">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#FEE2E2' }}>
            <Shield size={20} style={{ color: '#DC2626' }} fill="#FECACA" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 leading-tight">You&apos;re not alone</h3>
            <p className="text-[11px] text-gray-500">Step {step} of 3 — private unless you choose otherwise</p>
          </div>
        </div>
        <p className="text-[10px] text-gray-400 mb-4 ml-[50px]">HiBFF never logs these calls or what you talk about.</p>

        {step === 1 && (
          <div className="space-y-2.5">
            <p className="text-sm font-semibold text-gray-800 mb-2">How are you doing right now?</p>
            <button onClick={() => pick('danger')} data-testid="safety-option-danger"
              className="w-full text-left p-3.5 rounded-2xl border-2 text-sm font-semibold transition-colors hover:bg-red-50"
              style={{ borderColor: '#FCA5A5', color: '#B91C1C' }}>
              I&apos;m in immediate danger
            </button>
            <button onClick={() => pick('struggling')} data-testid="safety-option-struggling"
              className="w-full text-left p-3.5 rounded-2xl border-2 text-sm font-semibold transition-colors hover:bg-orange-50"
              style={{ borderColor: '#FDBA74', color: '#C2410C' }}>
              I&apos;m really struggling and need support
            </button>
            <button onClick={() => pick('talk')} data-testid="safety-option-talk"
              className="w-full text-left p-3.5 rounded-2xl border-2 text-sm font-semibold transition-colors hover:bg-purple-50"
              style={{ borderColor: '#D8B4FE', color: '#7E22CE' }}>
              I just want to talk to someone
            </button>
          </div>
        )}

        {step === 2 && (
          <div>
            {urgency === 'danger' && (
              <a href={`tel:${regionData.emergency.tel}`} data-testid="safety-call-emergency"
                onClick={() => audit('emergency_call_clicked', { tel: regionData.emergency.tel, device: onPhone ? 'phone' : 'desktop' })}
                className="flex items-center justify-center gap-2 w-full py-3.5 rounded-2xl text-white font-bold text-base mb-3 shadow-md"
                style={{ backgroundColor: '#DC2626' }}>
                <Phone size={18} /> {regionData.emergency.label}
              </a>
            )}
            <p className="text-sm font-semibold text-gray-800 mb-2">
              {urgency === 'talk' ? 'People who will listen, right now — free to call:' : 'Free, confidential helplines near you:'}
            </p>
            {!onPhone && (
              <p className="text-[11px] text-gray-500 mb-2">
                On a computer? Click a number to <strong>copy it</strong>, or scan the square with your phone camera — it dials for you.
              </p>
            )}
            <div className="space-y-2 mb-3">
              {regionData.lines.map(h => (
                <a key={h.name} href={`tel:${h.tel}`} onClick={() => handleLineClick(h)}
                  className="flex items-center justify-between gap-3 p-3 rounded-2xl border transition-colors hover:bg-orange-50"
                  style={{ borderColor: '#FDE4C8', backgroundColor: '#FFFBF5' }}
                  data-testid={`helpline-${h.tel}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-gray-900">{h.name}</p>
                    <p className="text-xs text-gray-500">{h.detail}</p>
                    <p className="text-base font-extrabold mt-0.5" style={{ color: '#DC2626' }}>{h.number}</p>
                    {copiedTel === h.tel && (
                      <p className="text-[11px] font-semibold flex items-center gap-1 mt-0.5" style={{ color: '#16A34A' }} data-testid={`helpline-copied-${h.tel}`}>
                        <Check size={12} /> Number copied — dial it from any phone
                      </p>
                    )}
                  </div>
                  {!onPhone && (
                    <div className="flex flex-col items-center flex-shrink-0" data-testid={`helpline-qr-${h.tel}`}>
                      <div className="p-1 bg-white rounded-lg border border-gray-200">
                        <QRCodeSVG value={`tel:${h.tel}`} size={54} />
                      </div>
                      <span className="text-[9px] text-gray-400 mt-0.5">Scan to call</span>
                    </div>
                  )}
                </a>
              ))}
              <a href="https://findahelpline.com" target="_blank" rel="noreferrer"
                onClick={() => audit('findahelpline_clicked', {})}
                className="flex items-center justify-center gap-2 p-3.5 rounded-2xl text-sm font-bold text-white shadow-md transition-transform hover:scale-[1.01]"
                style={{ background: 'linear-gradient(90deg, #7B4FD8, #9A6CF0)' }}
                data-testid="helpline-global">
                <Globe size={17} />
                {regionData.lines.length ? 'Somewhere else? Find a helpline in your country →' : 'Find a free helpline in your country →'}
              </a>
            </div>
            <p className="text-[11px] text-gray-400 leading-snug mb-3" data-testid="safety-disclaimer">{DISCLAIMER}</p>
            <div className="flex items-center justify-between">
              {!emergency ? (
                <button onClick={() => setStep(1)} className="text-xs text-gray-400 flex items-center gap-1"><ChevronLeft size={13} /> Back</button>
              ) : <span />}
              <button onClick={() => { setStep(3); audit('message_step_opened', {}); }} data-testid="safety-next-script"
                className="px-4 py-2 rounded-xl text-sm font-semibold text-white flex items-center gap-1.5" style={{ backgroundColor: '#7B4FD8' }}>
                <MessageCircle size={15} /> Message a trusted adult
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <p className="text-sm font-semibold text-gray-800 mb-2">Send this to a parent, carer or trusted adult:</p>
            <div className="p-3.5 rounded-2xl text-sm text-gray-800 mb-2" style={{ backgroundColor: '#FFF5EA', border: '1px solid #F5C99F' }} data-testid="safety-script-text">
              {SCRIPT_TEXT}
            </div>
            <p className="text-[11px] text-gray-500 mb-3">Make it yours — add their name or change any words before you send it.</p>
            <button onClick={copyScript} data-testid="safety-copy-script"
              className="w-full py-3 rounded-2xl text-sm font-bold text-white flex items-center justify-center gap-2 mb-2"
              style={{ backgroundColor: copied ? '#16A34A' : '#F5831F' }}>
              {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? 'Copied — now paste it into a text' : 'Copy message'}
            </button>
            <div className="flex items-center justify-between">
              <button onClick={() => setStep(2)} className="text-xs text-gray-400 flex items-center gap-1"><ChevronLeft size={13} /> Back</button>
              <p className="text-[11px] text-gray-400 flex items-center gap-1"><Heart size={11} fill="currentColor" className="text-red-400" /> HiBFF never shares this unless you send it</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SafetyNetModal;
